import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Globe, Camera, Loader2, ImagePlus } from "lucide-react";
import { fileToJpegDataUrl } from "@/lib/imageCompress";

const TXT = {
  fr: { web: "Photo web", mine: "Ma photo", replace: "Remplacer par ma photo", err: "Envoi de la photo impossible" },
  en: { web: "Web photo", mine: "My photo", replace: "Replace with my photo", err: "Could not upload the photo" },
  es: { web: "Foto web", mine: "Mi foto", replace: "Sustituir por mi foto", err: "No se pudo subir la foto" },
  pt: { web: "Foto web", mine: "A minha foto", replace: "Substituir pela minha foto", err: "Não foi possível enviar a foto" },
};

/** Photo choice for a tasting: web photo / own photo toggle + "replace with my photo" upload. */
export default function TastingPhotoControls({ form, setForm, lang = "fr", testIdPrefix = "tasting-photo" }) {
  const T = TXT[lang] || TXT.fr;
  const { user } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToJpegDataUrl(file);
      const { data } = await api.post("/tastings/photo", { image_base64: dataUrl });
      setForm((f) => ({
        ...f,
        web_photo_url: f.web_photo_url || (String(f.photo_url || "").startsWith("http") ? f.photo_url : null),
        user_photo_url: data.photo_url,
        photo_url: data.photo_url,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.err);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const pill = (active) => `inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[#1F3A5F] text-white border-[#1F3A5F]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"}`;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testIdPrefix}-controls`}>
      {form.web_photo_url && form.user_photo_url && (
        <>
          <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: f.web_photo_url }))}
                  data-testid={`${testIdPrefix}-use-web`} className={pill(form.photo_url === form.web_photo_url)}>
            <Globe className="w-3 h-3" /> {T.web}
          </button>
          <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: f.user_photo_url }))}
                  data-testid={`${testIdPrefix}-use-mine`} className={pill(form.photo_url === form.user_photo_url)}>
            <Camera className="w-3 h-3" /> {T.mine}
          </button>
        </>
      )}
      {user && (
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testIdPrefix}-replace`}
                className={pill(false)}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />} {T.replace}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testIdPrefix}-replace-input`}
             onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}
